import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register({ ...form, role: "patient" });
      toast.success("Account created. Welcome to Gutvibe Health.");
      nav("/onboarding");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "string") {
        toast.error(detail);
      } else if (Array.isArray(detail) && detail.length) {
        // Pydantic v2 error array: [{loc:[...], msg:"...", ...}]
        toast.error(detail.map((d) => d.msg || "Invalid input").join(" · "));
      } else {
        toast.error("Registration failed");
      }
    } finally { setLoading(false); }
  };

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-bone">
      <div className="hidden md:flex relative bg-ayurveda text-bone p-12 flex-col justify-between grain">
        <Link to="/" className="font-heritage text-3xl">Gutvibe</Link>
        <div>
          <div className="overline text-terracotta mb-4">Create account</div>
          <h1 className="font-heritage text-5xl leading-tight">Your journey<br/>begins with a hello.</h1>
          <p className="mt-6 text-bone/70 max-w-md">Join a consent-first, voice-first Gutvibe Health companion — Priya, our Malayali AI — built for India — grounded in evidence, respectful of tradition.</p>
        </div>
        <div className="text-xs text-bone/50">You can revoke any permission any time.</div>
      </div>
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="register-form">
          <div>
            <h2 className="font-heritage text-4xl text-ayurveda">Create your account</h2>
            <p className="text-sm text-charcoal/60 mt-2">Takes less than a minute.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="name">Full name</Label>
            <Input id="name" value={form.name} onChange={update("name")} required data-testid="register-name" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={form.email} onChange={update("email")} required data-testid="register-email" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone">Phone (optional)</Label>
            <Input id="phone" value={form.phone} onChange={update("phone")} placeholder="+91…" data-testid="register-phone" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password (min 8)</Label>
            <Input id="password" type="password" minLength={8} value={form.password} onChange={update("password")} required data-testid="register-password" />
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-ayurveda hover:bg-ayurveda/90 text-bone h-11" data-testid="register-submit">
            {loading ? "Creating…" : "Create account"}
          </Button>
          <div className="text-sm text-charcoal/70">
            Already have an account? <Link to="/login" className="text-ayurveda font-medium underline underline-offset-4" data-testid="link-login">Sign in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
